import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getWallet } from "../api/staff.api";
import { createConsultationBooking } from "../api/bookings.api";
import { submitBookingWithPayment } from "../utils/bookingPayment";
import { mergeWalletResponse } from "../utils/wallet";
import { apiErrorMessage } from "../utils/apiError";
import { getAssetUrl } from "../api/config";
import { getStaffId } from "../utils/storage";
import { toast } from "../Components/Toast";
import {
  getDoctorWalletBalance,
  getCategoryWalletAllocation,
  formatWalletAmount,
} from "../utils/wallet";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import {
  FaArrowLeft, FaStethoscope, FaUser, FaFileAlt, FaCalendarAlt,
  FaMapMarkerAlt, FaVideo, FaCheck,
} from "react-icons/fa";

const doctorName = (doctor) => {
  const n = doctor?.name || "Doctor";
  return /^dr\.?\s/i.test(n) ? n : `Dr. ${n}`;
};

const MAX_TEXT_LEN = 100;

const formatDateLong = (ymd) => {
  const d = new Date(ymd + "T00:00:00");
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" });
};

const ReviewConfirmPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const staffId = getStaffId();
  const { doctor, consultationType, selectedDate, selectedSlot, patient } = location.state || {};

  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [walletData, setWalletData] = useState(location.state?.walletData || null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!doctor || !selectedDate || !selectedSlot || !patient) {
      navigate("/consultations", { replace: true });
    }
  }, [doctor, selectedDate, selectedSlot, patient, navigate]);

  useEffect(() => {
    if (!staffId || walletData) return;
    getWallet(staffId)
      .then((res) => setWalletData(res.data))
      .catch(() => {});
  }, [staffId, walletData]);

  if (!doctor || !selectedDate || !selectedSlot || !patient) return null;

  const fee = Number(doctor.consultation_fee) || 0;
  const walletBal = getDoctorWalletBalance(walletData);
  const categoryAlloc = getCategoryWalletAllocation(walletData, "consultation");
  const walletUse = Math.min(fee, walletBal);
  const canPayWithWallet = walletBal >= fee;
  const shortfall = Math.max(0, fee - walletBal);

  const bookingPayload = () => {
    const payload = {
      doctorId: doctor._id,
      date: selectedDate,
      timeSlot: selectedSlot.timeSlot,
      type: consultationType,
      reason: reason.trim(),
      notes: notes || "",
      useWallet: true,
    };
    if (patient.familyMemberId) payload.familyMemberId = patient.familyMemberId;
    return payload;
  };

  const goConfirmed = (bookingResponse) => {
    toast.success("Consultation booked successfully.");
    navigate("/consultations/confirmed", {
      replace: true,
      state: {
        doctor,
        consultationType,
        selectedDate,
        selectedSlot,
        patient,
        reason,
        notes,
        booking: bookingResponse,
        walletUsed: walletUse,
        amountPaid: fee,
      },
    });
  };

  const handleConfirm = async () => {
    if (!reason.trim()) {
      setError("Please provide a reason for visit.");
      return;
    }
    if (reason.length > MAX_TEXT_LEN || notes.length > MAX_TEXT_LEN) {
      setError("Reason and notes must be 100 characters or less.");
      return;
    }
    setError("");
    setProcessing(true);
    try {
      const data = await submitBookingWithPayment({
        post: (body) => createConsultationBooking(staffId, body),
        body: bookingPayload(),
        description: `Consultation: ${doctorName(doctor)}`,
        onWallet: (patch) => setWalletData((prev) => mergeWalletResponse(prev, patch)),
      });
      goConfirmed(data);
    } catch (err) {
      const msg = apiErrorMessage(err, "Booking failed. Please try again.");
      if (msg === "Payment cancelled") {
        toast.error("Payment cancelled");
      } else {
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setProcessing(false);
    }
  };

  const isVideo = consultationType === "Online";

  return (
    <div className="bg-surface-low min-h-screen flex flex-col">
      <Header />
      <div className="flex flex-col min-h-screen pb-16 lg:pb-0 flex-1">
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-on-surface-variant hover:text-primary mb-3"
          >
            <FaArrowLeft size={12} /> Back to Selection
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-6">Review & Confirm</h1>

          {error && <p className="text-error text-sm mb-4">{error}</p>}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-surface-lowest rounded-xl border border-surface-container p-5">
                <h2 className="flex items-center gap-2 font-semibold text-primary mb-4">
                  <FaStethoscope /> Consultation Details
                </h2>
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src={getAssetUrl(doctor.image) || "/logo.png"}
                    alt={doctorName(doctor)}
                    className="w-14 h-14 rounded-lg object-cover"
                    onError={(e) => { e.target.src = "/logo.png"; }}
                  />
                  <div>
                    <p className="font-bold text-primary">{doctorName(doctor)}</p>
                    <p className="text-sm text-on-surface-variant">{doctor.specialization || "Specialist"}</p>
                    <span className="inline-flex items-center gap-1 mt-1 text-xs bg-surface-low px-2 py-0.5 rounded-full">
                      {isVideo && <FaVideo />}
                      {isVideo ? "Video Consultation" : "In-Person"}
                    </span>
                  </div>
                </div>
                {doctor.address && (
                  <p className="text-sm text-on-surface-variant flex items-start gap-2">
                    <FaMapMarkerAlt className="mt-0.5 shrink-0" /> {doctor.address}
                  </p>
                )}
              </div>

              <div className="bg-surface-lowest rounded-xl border border-surface-container p-5">
                <h2 className="flex items-center gap-2 font-semibold text-primary mb-4">
                  <FaUser /> Patient Information
                </h2>
                <p className="text-xs text-on-surface-variant uppercase">Name</p>
                <p className="font-medium mb-3">{patient.name}</p>

                <label className="text-sm font-semibold text-primary">Reason for Visit *</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value.slice(0, MAX_TEXT_LEN))}
                  maxLength={MAX_TEXT_LEN}
                  rows={3}
                  placeholder="E.g., Routine follow-up on blood pressure medication..."
                  className="w-full border border-surface-container rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-on-surface-variant text-right mt-1">{reason.length}/{MAX_TEXT_LEN}</p>
              </div>

              <div className="bg-surface-lowest rounded-xl border border-surface-container p-4">
                <h2 className="flex items-center gap-2 font-semibold text-primary mb-3">
                  <FaFileAlt /> Quick Notes
                </h2>
                <p className="text-sm text-on-surface-variant mb-2">
                  Optional notes for the doctor (allergies, recent tests, etc.).
                </p>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value.slice(0, MAX_TEXT_LEN))}
                  maxLength={MAX_TEXT_LEN}
                  rows={3}
                  placeholder="E.g., Recently completed blood work..."
                  className="w-full border border-surface-container rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-on-surface-variant text-right mt-1">{notes.length}/{MAX_TEXT_LEN}</p>
              </div>

              <div className="bg-surface-lowest rounded-xl border border-surface-container p-4">
                <h2 className="flex items-center gap-2 font-semibold text-primary mb-3">
                  <FaCalendarAlt /> Date & Time
                </h2>
                <div className="bg-primary-fixed/40 rounded-lg p-4">
                  <p className="font-semibold">{formatDateLong(selectedDate)}</p>
                  <p className="text-2xl font-bold text-on-primary-fixed-var">{selectedSlot.timeSlot}</p>
                  <p className="text-sm text-on-surface-variant">Duration: 30 minutes</p>
                </div>
                {isVideo ? (
                  <p className="italic text-xs text-on-surface-variant mt-3">
                    For online consultations, you will receive an email with the meeting joining links.
                  </p>
                ) : (
                  <p className="italic text-xs text-on-surface-variant mt-3">
                    For in-person consultations, please arrive 10 minutes early at the clinic address shown above.
                  </p>
                )}
              </div>
            </div>

            <aside className="lg:sticky lg:top-20 h-fit bg-surface-lowest rounded-xl border border-surface-container p-4">
              <h2 className="font-semibold text-primary mb-4">Payment Summary</h2>
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span>Consultation Fee</span>
                  <span>₹{fee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-error">
                  <span>Corporate Wallet Deduction</span>
                  <span>-₹{walletUse.toFixed(2)}</span>
                </div>
                <hr className="border-surface-container" />
                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  <span>₹{fee.toFixed(2)}</span>
                </div>
              </div>
              <p className="mt-4 text-sm text-on-surface-variant">
                Available corporate balance:{" "}
                <span className="font-semibold text-on-primary-fixed-var">₹{formatWalletAmount(walletBal)}</span>
              </p>
              {categoryAlloc != null && categoryAlloc !== walletBal && (
                <p className="mt-1 text-xs text-on-surface-variant">
                  Category allocation: ₹{formatWalletAmount(categoryAlloc)} (total balance used for booking)
                </p>
              )}
              <hr className="border-surface-container my-4" />
              <p className="text-sm text-on-surface-variant">Paid from wallet</p>
              <p className="text-3xl font-bold text-primary mb-2">₹{walletUse.toFixed(2)}</p>
              {!canPayWithWallet && (
                <p className="text-sm text-error mb-4">
                  Shortfall: ₹{shortfall.toFixed(2)}.{" "}
                  <Link to="/wallet" className="underline font-medium">View wallet</Link>
                  <span className="block mt-1 text-on-surface-variant">
                    Remaining ₹{shortfall.toFixed(2)} will be paid online.
                  </span>
                </p>
              )}
              <button
                onClick={handleConfirm}
                disabled={processing || !reason.trim()}
                className="w-full py-3 rounded-lg bg-primary text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="w-5 h-5 rounded-full border border-white flex items-center justify-center">
                  <FaCheck size={10} />
                </span>
                {processing ? "Processing..." : shortfall > 0 ? "Pay & Book" : "Confirm & Book"}
              </button>
              <p className="text-xs text-on-surface-variant mt-3 text-center">
                By confirming, you agree to our{" "}
                <a href="/termsandconditions" className="underline">cancellation policy</a>.
              </p>
            </aside>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default ReviewConfirmPage;
